import { memo, type PropsWithChildren } from 'react';
import styled from 'styled-components';

import { LINK_COLOR } from '@wecasa/common/src/constants/link';
import type { LinkSize, LinkVariant } from '@wecasa/common/src/types/components';

import Text from '../base-text/variants/text';
import { linkStyle } from './common';

const StyledLink = styled.a<{ $color: string }>`
  ${({ $color }) => linkStyle($color)}
`;

export type SimpleLinkProps = PropsWithChildren & {
  title?: string;
  href?: string;
  openInNewTab?: boolean;
  className?: string;
  variant?: LinkVariant;
  size?: LinkSize;
};

const generateMatchingText = ({
  size,
  color,
  children,
}: Pick<SimpleLinkProps, 'size' | 'children'> & { color: string }) => {
  switch (size) {
    case 'sm':
      return <Text.SMMedium color={color}>{children}</Text.SMMedium>;
    case 'md':
      return <Text.MDMedium color={color}>{children}</Text.MDMedium>;
    case 'xs':
      return <Text.XSMedium color={color}>{children}</Text.XSMedium>;
    case 'xxs':
      return <Text.XXSMedium color={color}>{children}</Text.XXSMedium>;
    default:
      return <Text.MDMedium color={color}>{children}</Text.MDMedium>;
  }
};

const SimpleLink = ({
  children,
  title,
  href,
  openInNewTab = false,
  className,
  variant = 'normal',
  size = 'md',
}: SimpleLinkProps) => {
  const color = LINK_COLOR[variant];
  return (
    <StyledLink
      className={className}
      $color={color}
      href={href}
      title={title}
      target={openInNewTab ? '_blank' : undefined}
      rel={openInNewTab ? 'noopener' : undefined}
    >
      {generateMatchingText({ children, size, color })}
    </StyledLink>
  );
};

export default memo(SimpleLink);
